/**
 * Vücut kompozisyon analiz raporu motoru — kilo detayındaki rapor görünümünün
 * TEK veri kaynağı. Ölçüm kaydındaki {key,value,unit} listesinden bölümlü
 * rapor modeli üretir; aralıklar client-side standart BIA referans
 * bantlarından (cinsiyet/boy formülleriyle) hesaplanır. Pure — UI içermez;
 * mobil constants/bodyReport.ts'in birebir JS kopyasıdır; sabitler iki
 * dosyada senkron tutulur.
 */

// Rapor etiketi tablosu — mobil BODY_FAT_FIELDS + rapora özgü ekler.
const LABELS = {
  weight: "Kilo",
  heartRate: "Nabız",
  bmi: "Vücut Kitle İndeksi",
  bodyFatPct: "Yağ Oranı",
  bodyFatKg: "Yağ Kütlesi",
  waterPct: "Su Oranı",
  muscleKg: "Kas Kütlesi",
  musclePct: "Kas Oranı",
  skeletalMusclePct: "İskelet Kası Oranı",
  subcutaneousFatPct: "Deri Altı Yağ Oranı",
  leanBodyMassKg: "Yağsız Kütle",
  boneKg: "Kemik Kütlesi",
  visceralFat: "Visseral Yağ",
  bmr: "Bazal Metabolizma",
  proteinPct: "Protein Oranı",
  bodyAge: "Vücut Yaşı",
  idealWeightKg: "İdeal Kilo",
  obesityPct: "Obezite",
  bodyScore: "Vücut Skoru",
  skeletalMuscleKg: "İskelet Kas Kütlesi",
  subcutaneousFatKg: "Deri Altı Yağ Kütlesi",
  proteinKg: "Protein Kütlesi",
  mineralKg: "Mineral Kütlesi",
  cellMassKg: "Hücre Kütlesi",
  waterKg: "Su Kütlesi",
  waterECWKg: "Hücre Dışı Su",
  waterICWKg: "Hücre İçi Su",
  smi: "İskelet Kası İndeksi",
  whr: "Bel/Kalça Oranı",
  dci: "Günlük Kalori İhtiyacı",
  standardWeightKg: "Standart Kilo",
  controlWeightKg: "Kilo Kontrolü",
  fatControlKg: "Yağ Kontrolü",
  muscleControlKg: "Kas Kontrolü",
  fatKgLeftArm: "Sol Kol Yağ Kütlesi",
  fatKgRightArm: "Sağ Kol Yağ Kütlesi",
  fatKgLeftLeg: "Sol Bacak Yağ Kütlesi",
  fatKgRightLeg: "Sağ Bacak Yağ Kütlesi",
  fatKgTrunk: "Gövde Yağ Kütlesi",
  fatRateLeftArm: "Sol Kol Yağ Oranı",
  fatRateRightArm: "Sağ Kol Yağ Oranı",
  fatRateLeftLeg: "Sol Bacak Yağ Oranı",
  fatRateRightLeg: "Sağ Bacak Yağ Oranı",
  fatRateTrunk: "Gövde Yağ Oranı",
  muscleKgLeftArm: "Sol Kol Kas Kütlesi",
  muscleKgRightArm: "Sağ Kol Kas Kütlesi",
  muscleKgLeftLeg: "Sol Bacak Kas Kütlesi",
  muscleKgRightLeg: "Sağ Bacak Kas Kütlesi",
  muscleKgTrunk: "Gövde Kas Kütlesi",
  muscleRateLeftArm: "Sol Kol Kas Oranı",
  muscleRateRightArm: "Sağ Kol Kas Oranı",
  muscleRateLeftLeg: "Sol Bacak Kas Oranı",
  muscleRateRightLeg: "Sağ Bacak Kas Oranı",
  muscleRateTrunk: "Gövde Kas Oranı",
};

const r1 = (v) => Math.round(v * 10) / 10;

function byGender(m, f) {
  return (ctx) => (ctx.gender === "Erkek" ? m : ctx.gender === "Kadın" ? f : null);
}

// Standart kilo (BMI 22) — kas/yağsız kütle bantları buna oranlanır.
function stdWeight(ctx) {
  if (!ctx.heightCm) return null;
  const h = ctx.heightCm / 100;
  return 22 * h * h;
}

function ofStdWeight(mMin, mMax, fMin, fMax) {
  return (ctx) => {
    const sw = stdWeight(ctx);
    if (!sw || !ctx.gender) return null;
    const [lo, hi] = ctx.gender === "Erkek" ? [mMin, mMax] : [fMin, fMax];
    return { min: r1(sw * lo), max: r1(sw * hi) };
  };
}

function ofWeight(mMin, mMax, fMin, fMax) {
  return (ctx) => {
    if (!ctx.weightKg || !ctx.gender) return null;
    const [lo, hi] = ctx.gender === "Erkek" ? [mMin, mMax] : [fMin, fMax];
    return { min: r1(ctx.weightKg * lo), max: r1(ctx.weightKg * hi) };
  };
}

// Ölçülen BMR'nin karşılaştırma bandı: Mifflin-St Jeor ±%10.
function bmrRange(ctx) {
  if (!ctx.weightKg || !ctx.heightCm || !ctx.age || !ctx.gender) return null;
  const base =
    10 * ctx.weightKg + 6.25 * ctx.heightCm - 5 * ctx.age + (ctx.gender === "Erkek" ? 5 : -161);
  return { min: Math.round(base * 0.9), max: Math.round(base * 1.1) };
}

// Yaygın BIA referans bantları (Lefu rapor düzeniyle hizalı). Formüllü olanlar
// profil eksikse null döner → satır bar'sız çizilir.
const RANGES = {
  weight: (ctx) => {
    if (!ctx.heightCm) return null;
    const h = ctx.heightCm / 100;
    return { min: r1(18.5 * h * h), max: r1(25 * h * h) };
  },
  bmi: { min: 18.5, max: 25 },
  heartRate: { min: 55, max: 100 },
  bmr: bmrRange,
  obesityPct: { min: 90, max: 110 },
  proteinPct: { min: 12.9, max: 15.8 },
  whr: byGender({ min: 0.8, max: 0.9 }, { min: 0.7, max: 0.8 }),
  bodyFatPct: byGender({ min: 10, max: 20 }, { min: 20, max: 30 }),
  bodyFatKg: ofWeight(0.1, 0.2, 0.2, 0.3),
  subcutaneousFatPct: byGender({ min: 8.6, max: 16.7 }, { min: 18.5, max: 26.7 }),
  subcutaneousFatKg: ofStdWeight(0.085, 0.166, 0.18, 0.27),
  visceralFat: { min: 1, max: 9 },
  leanBodyMassKg: ofStdWeight(0.73, 0.91, 0.62, 0.8),
  waterPct: byGender({ min: 55, max: 65 }, { min: 45, max: 60 }),
  waterKg: ofWeight(0.48, 0.6, 0.42, 0.55),
  waterECWKg: ofWeight(0.185, 0.225, 0.16, 0.2),
  waterICWKg: ofWeight(0.3, 0.37, 0.26, 0.33),
  muscleKg: ofStdWeight(0.72, 0.99, 0.6, 0.85),
  skeletalMuscleKg: ofStdWeight(0.43, 0.53, 0.36, 0.46),
  skeletalMusclePct: byGender({ min: 37.1, max: 45.4 }, { min: 28, max: 35 }),
  boneKg: byGender({ min: 3, max: 4 }, { min: 2.2, max: 3 }),
  smi: byGender({ min: 7, max: 10.5 }, { min: 5.7, max: 8.5 }),
};

// Kilo-benzeri metriklerde durum dili "Zayıf/Kilolu"; kalanlarda "Düşük/Yüksek".
const WEIGHT_LIKE = new Set(["weight", "bmi", "bodyFatKg", "bodyFatPct", "obesityPct"]);
const STATUS_TR = { low: "Düşük", normal: "Standart", high: "Yüksek" };
const STATUS_TR_WEIGHT = {
  low: "Zayıf",
  normal: "Standart",
  high: "Kilolu",
};

export const BODY_TYPE_MATRIX = {
  high: { low: "Fazla Kilolu", normal: "Kilolu ve Kaslı", high: "Kaslı ve Kilolu" },
  normal: { low: "Hareketsiz", normal: "Standart", high: "Standart ve Kaslı" },
  low: { low: "Zayıf", normal: "Zayıf ve Kaslı", high: "Kaslı" },
};

export const BODY_REPORT_DISCLAIMER =
  "Tüm parametreler biyoelektriksel empedans analizi (BIA) teknolojisiyle ölçülmüştür. " +
  "Değerler sağlık ve fitness takibi için referans amaçlıdır; tıbbi tanı veya tedavi " +
  "kararlarına temel oluşturmaz. Aralıklar yaygın BIA referans bantlarıdır.";

function resolveRange(key, ctx) {
  const rule = RANGES[key];
  if (!rule) return null;
  return typeof rule === "function" ? rule(ctx) : rule;
}

function buildRow(key, map, ctx) {
  const v = map.get(key);
  if (!v) return null;
  const row = {
    key,
    label: LABELS[key] || key,
    value: v.value,
    unit: v.unit === "-" ? "" : v.unit,
  };
  const range = resolveRange(key, ctx);
  if (range) {
    row.range = range;
    row.status = v.value < range.min ? "low" : v.value > range.max ? "high" : "normal";
    row.statusLabel = (WEIGHT_LIKE.has(key) ? STATUS_TR_WEIGHT : STATUS_TR)[row.status];
  }
  return row;
}

const REGION_DEFS = [
  { region: "leftArm", label: "Sol Kol" },
  { region: "rightArm", label: "Sağ Kol" },
  { region: "trunk", label: "Gövde" },
  { region: "leftLeg", label: "Sol Bacak" },
  { region: "rightLeg", label: "Sağ Bacak" },
];

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

function buildRegions(prefixKg, prefixRate, map) {
  const regions = [];
  for (const def of REGION_DEFS) {
    const kg = map.get(`${prefixKg}${cap(def.region)}`)?.value;
    const pct = map.get(`${prefixRate}${cap(def.region)}`)?.value;
    if (kg !== undefined || pct !== undefined) regions.push({ ...def, kg, pct });
  }
  return regions;
}

export function buildBodyReport(values, profile = {}) {
  const map = new Map(
    values
      .filter((v) => typeof v.value === "number" && Number.isFinite(v.value))
      .map((v) => [v.key, v])
  );
  const weightKg = map.get("weight")?.value ?? null;
  const ctx = {
    heightCm: profile.heightCm ?? null,
    age: profile.age ?? null,
    gender: profile.gender ?? null,
    weightKg,
  };
  const rows = (keys) => keys.map((k) => buildRow(k, map, ctx)).filter((r) => r !== null);

  const sections = [];

  // 1 — Başlık (puan + kilo/BMI/vücut yaşı)
  const headerRows = rows(["weight", "bmi", "bodyAge"]);
  const score = map.get("bodyScore")?.value;
  if (score !== undefined || headerRows.length > 0) {
    sections.push({
      id: "header",
      kind: "header",
      title: "Vücut Kompozisyon Analizi",
      score,
      rows: headerRows,
    });
  }

  // 2 — Vücut Bileşimi (donut: su/yağ/protein/mineral → ağırlığa oran)
  const compRows = rows(["waterKg", "bodyFatKg", "proteinKg", "mineralKg"]);
  if (compRows.length > 0) {
    sections.push({
      id: "composition",
      kind: "donut",
      title: "Vücut Bileşimi",
      weightKg: weightKg ?? undefined,
      rows: compRows,
    });
  }

  // 3 — Genel Vücut Durumu
  const generalRows = rows(["bmi", "heartRate", "bmr", "obesityPct", "proteinPct", "whr"]);
  if (generalRows.length > 0) {
    sections.push({ id: "general", kind: "bars", title: "Genel Vücut Durumu", rows: generalRows });
  }

  // 4 — Fiziksel Durum Önerileri
  const adviceRows = rows([
    "idealWeightKg",
    "standardWeightKg",
    "controlWeightKg",
    "fatControlKg",
    "muscleControlKg",
    "bodyAge",
    "dci",
  ]);
  if (adviceRows.length > 0) {
    sections.push({ id: "advice", kind: "pairs", title: "Fiziksel Durum Önerileri", rows: adviceRows });
  }

  // 5 — Vücut Tipi Analizi (yağ% × kas durumu matrisi)
  const fatRow = buildRow("bodyFatPct", map, ctx);
  const muscleRow = buildRow("muscleKg", map, ctx) ?? buildRow("musclePct", map, ctx);
  if (fatRow?.status && muscleRow?.status) {
    sections.push({
      id: "bodyType",
      kind: "matrix",
      title: "Vücut Tipi Analizi",
      fatLevel: fatRow.status,
      muscleLevel: muscleRow.status,
      label: BODY_TYPE_MATRIX[fatRow.status][muscleRow.status],
    });
  }

  // 6 — Kas-İskelet Durumu
  const muscleRows = rows(["muscleKg", "skeletalMuscleKg", "skeletalMusclePct", "boneKg", "smi"]);
  if (muscleRows.length > 0) {
    sections.push({ id: "muscle", kind: "bars", title: "Kas-İskelet Durumu", rows: muscleRows });
  }

  // 7 — Yağ Analizi
  const fatRows = rows([
    "bodyFatPct",
    "subcutaneousFatKg",
    "subcutaneousFatPct",
    "visceralFat",
    "leanBodyMassKg",
  ]);
  if (fatRows.length > 0) {
    sections.push({ id: "fat", kind: "bars", title: "Yağ Analizi", rows: fatRows });
  }

  // 8-9 — Bölgesel Yağ / Kas
  const regionalFat = buildRegions("fatKg", "fatRate", map);
  if (regionalFat.length > 0) {
    sections.push({ id: "regionalFat", kind: "regions", title: "Bölgesel Yağ Analizi", regions: regionalFat });
  }
  const regionalMuscle = buildRegions("muscleKg", "muscleRate", map);
  if (regionalMuscle.length > 0) {
    sections.push({
      id: "regionalMuscle",
      kind: "regions",
      title: "Bölgesel Kas Analizi",
      regions: regionalMuscle,
    });
  }

  // 10 — Vücut Hücre ve Su Durumu
  const waterRows = rows(["waterKg", "waterECWKg", "waterICWKg", "cellMassKg"]);
  if (waterRows.length > 0) {
    sections.push({ id: "water", kind: "bars", title: "Vücut Hücre ve Su Durumu", rows: waterRows });
  }

  // 11 — Sorumluluk reddi (rapor doluysa)
  if (sections.length > 0) {
    sections.push({
      id: "disclaimer",
      kind: "text",
      title: "Sorumluluk Reddi",
      text: BODY_REPORT_DISCLAIMER,
    });
  }

  return { sections };
}

// Kullanıcı/hasta kaydından rapor profili — quick-measure'daki tartı
// doğrulamalarıyla (boy 90–220, cinsiyet Erkek/Kadın) aynı kurallar.
export function profileFromUser(user) {
  const heightCm =
    typeof user?.height === "number" && user.height >= 90 && user.height <= 220
      ? user.height
      : null;
  let age = null;
  if (user?.birthDay) {
    const birth = new Date(user.birthDay);
    if (!Number.isNaN(birth.getTime())) {
      const now = new Date();
      age =
        now.getFullYear() -
        birth.getFullYear() -
        (now < new Date(now.getFullYear(), birth.getMonth(), birth.getDate()) ? 1 : 0);
      if (age < 3 || age > 120) age = null;
    }
  }
  const gender = user?.gender === "Erkek" || user?.gender === "Kadın" ? user.gender : null;
  return { heightCm, age, gender };
}

// 64.19999694824219 → "64,2" (Türkçe ondalık virgül; tam sayılar sade kalır)
export function fmtReportNum(v) {
  const r = Math.round(v * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1).replace(".", ",");
}
